<template>
  <div
    class="sidebar"
    :class="{ collapsed: sidebarCollapsed, hovered: sidebarCollapsed && isHovering }"
    @mouseenter="handleSidebarMouseEnter"
    @mouseleave="handleSidebarMouseLeave"
  >
    <header>
      <div class="header-logo">
        <img
          v-if="showCollapsedFavicon"
          :src="settings.faviconUrl"
          :alt="settings?.siteName || 'Logo'"
          class="logo-icon-collapsed"
        >
        <img
          v-else-if="currentLogo"
          :src="currentLogo"
          :alt="settings?.siteName || 'Logo'"
          class="logo-image"
          @error="handleImageError"
        >
      </div>
    </header>

    <!-- Dashboard - não visível para AGENTE -->
    <router-link v-if="!isAgente" to="/dashboard" :class="selectedRoute('/dashboard') ? 'active' : ''">
      <i class="fa-solid fa-chart-pie"></i>
      <span>Dashboard</span>
    </router-link>

    <!-- Bancas com submenu - ADMIN -->
    <div 
      v-if="hasPermission('bancas')" 
      class="menu-group"
    >
      <div
        class="menu-title"
        :class="{ 'has-submenu': true, 'submenu-open': showBancasSubmenu }"
        @click="toggleSubmenu('bancas')"
      >
        <i class="fa-solid fa-building"></i>
        <span>Bancas</span>
        <i class="fa-solid fa-chevron-right submenu-arrow" :class="{ 'rotated': showBancasSubmenu }"></i>
      </div>
      <div class="submenu" :class="{ 'show': showBancasSubmenu }">
        <div class="submenu-content">
          <router-link to="/bancas" class="submenu-item" :class="{ 'active': $route.path === '/bancas' }">
            <i class="fa-solid fa-list"></i>
            <span>Listagem</span>
          </router-link>

          <router-link to="/regions" class="submenu-item" :class="{ 'active': $route.path === '/regions' }">
            <i class="fa-solid fa-earth-americas"></i>
            <span>Regiões</span>
          </router-link>
        </div>
      </div>
    </div>

    <!-- Regiões - ADMIN, GERENTE e BANCA (quando não há permissão para bancas) -->
    <router-link 
      v-if="hasPermission('regions') && !hasPermission('bancas')" 
      to="/regions" 
      :class="selectedRoute('/regions') ? 'active' : ''"
    >
      <i class="fa-solid fa-earth-americas"></i>
      <span>Regiões</span>
    </router-link>

    <!-- Loterias - ADMIN, GERENTE e BANCA -->
    <router-link 
      v-if="hasPermission('lotteries')" 
      to="/lotteries" 
      :class="selectedRoute('/lotteries') ? 'active' : ''"
    >
      <i class="fa-solid fa-clock"></i>
      <span>Loterias</span>
    </router-link>

    <!-- Cobradores com submenu - ADMIN, GERENTE, BANCA e COBRADOR -->
    <div 
      v-if="hasPermission('collectors')" 
      class="menu-group"
    >
      <div
        class="menu-title"
        :class="{ 'has-submenu': true, 'submenu-open': showCollectorsSubmenu }"
        @click="toggleSubmenu('collectors')"
      >
        <i class="fa-solid fa-user-check"></i>
        <span>Cobradores</span>
        <i class="fa-solid fa-chevron-right submenu-arrow" :class="{ 'rotated': showCollectorsSubmenu }"></i>
      </div>
      <div class="submenu" :class="{ 'show': showCollectorsSubmenu }">
        <div class="submenu-content">
          <router-link to="/collectors" class="submenu-item" :class="{ 'active': $route.path === '/collectors' }">
            <i class="fa-solid fa-list"></i>
            <span>Listagem</span>
          </router-link>

          <router-link to="/collector-commissions" class="submenu-item" :class="{ 'active': $route.path === '/collector-commissions' }">
            <i class="fa-solid fa-chart-line"></i>
            <span>Comissões</span>
          </router-link>

          <router-link to="/collector-history" class="submenu-item" :class="{ 'active': $route.path === '/collector-history' }">
            <i class="fa-solid fa-history"></i>
            <span>Histórico</span>
          </router-link>

          <router-link to="/collector-charges" class="submenu-item" :class="{ 'active': $route.path === '/collector-charges' }">
            <i class="fa-solid fa-hand-holding-dollar"></i>
            <span>Cobranças</span>
          </router-link>

          <router-link to="/collector-entries" class="submenu-item" :class="{ 'active': $route.path === '/collector-entries' }">
            <i class="fa-solid fa-handshake"></i>
            <span>Fechamento</span>
          </router-link>
        </div>
      </div>
    </div>

    <!-- Vendedores com submenu - ADMIN, GERENTE, BANCA e COBRADOR -->
    <div 
      v-if="hasPermission('sellers')" 
      class="menu-group"
    >
      <div
        class="menu-title"
        :class="{ 'has-submenu': true, 'submenu-open': showSubmenu }"
        @click="toggleSubmenu('sellers')"
      >
        <i class="fa-solid fa-user"></i>
        <span>Vendedores</span>
        <i class="fa-solid fa-chevron-right submenu-arrow" :class="{ 'rotated': showSubmenu }"></i>
      </div>
      <div class="submenu" :class="{ 'show': showSubmenu }">
        <div class="submenu-content">
          <router-link to="/sellers" :class="selectedRoute('/sellers') ? 'active' : ''" class="submenu-item">
            <i class="fa-solid fa-user-plus"></i>
            <span>Cadastro</span>
          </router-link>
          <router-link to="/sellers/bets" :class="selectedRoute('/sellers/bets') ? 'active' : ''" class="submenu-item">
            <i class="fa-solid fa-ticket"></i>
            <span>Bilhetes</span>
          </router-link>
          <router-link to="/sellers/entries" :class="selectedRoute('/sellers/entries') ? 'active' : ''" class="submenu-item">
            <i class="fa-solid fa-calculator"></i>
            <span>Fechamento</span>
          </router-link>
          <router-link to="/sellers/lottery-history" :class="selectedRoute('/sellers/lottery-history') ? 'active' : ''" class="submenu-item">
            <i class="fa-solid fa-history"></i>
            <span>Histórico por Loterias</span>
          </router-link>
        </div>
      </div>
    </div>

    <!-- Lançamentos - ADMIN, GERENTE, BANCA e COBRADOR -->
    <router-link 
      v-if="hasPermission('financialtransactions')" 
      to="/financialTransactions" 
      :class="selectedRoute('/financialTransactions') ? 'active' : ''"
    >
      <i class="fa-solid fa-dollar-sign"></i>
      <span>Lançamentos</span>
    </router-link>

    <!-- Fechamento - ADMIN, GERENTE, BANCA e COBRADOR -->
    <router-link 
      v-if="hasPermission('entries')" 
      to="/entries" 
      :class="selectedRoute('/entries') ? 'active' : ''"
    >
      <i class="fa-solid fa-circle-dollar-to-slot"></i>
      <span>Fechamento</span>
    </router-link>

    <!-- Resultados - ADMIN, GERENTE, BANCA e AGENTE -->
    <router-link 
      v-if="hasPermission('results')" 
      to="/results" 
      :class="selectedRoute('/results') ? 'active' : ''"
    >
      <i class="fa-solid fa-grip"></i>
      <span>Resultados</span>
    </router-link>

    <!-- Descargas com submenu - ADMIN, GERENTE e BANCA -->
    <div 
      v-if="hasPermission('descargas')" 
      class="menu-group"
    >
      <div
        class="menu-title"
        :class="{ 'has-submenu': true, 'submenu-open': showDescargasSubmenu }"
        @click="toggleSubmenu('descargas')"
      >
        <i class="fa-solid fa-download"></i>
        <span>Descargas</span>
        <i class="fa-solid fa-chevron-right submenu-arrow" :class="{ 'rotated': showDescargasSubmenu }"></i>
      </div>
      <div class="submenu" :class="{ 'show': showDescargasSubmenu }">
        <div class="submenu-content">
          <!-- Descarga Enviada: NÃO mostrar para Redescarga (OnlyDescarga=true E DescargaRegionId=null) -->
          <router-link 
            v-if="shouldShowDescargaEnviada" 
            to="/descargas-enviada" 
            class="submenu-item" 
            :class="{ 'active': $route.path === '/descargas-enviada' }"
          >
            <i class="fa-solid fa-paper-plane"></i>
            <span>Descarga Enviada</span>
          </router-link>

          <!-- Descarga Recebida: NÃO mostrar para Normal (OnlyDescarga=false) -->
          <router-link 
            v-if="shouldShowDescargaRecebida" 
            to="/descargas-recebida" 
            class="submenu-item" 
            :class="{ 'active': $route.path === '/descargas-recebida' }"
          >
            <i class="fa-solid fa-inbox"></i>
            <span>Descarga Recebida</span>
          </router-link>

          <router-link to="/historico-descarga" class="submenu-item" :class="{ 'active': $route.path === '/historico-descarga' }">
            <i class="fa-solid fa-history"></i>
            <span>Histórico de Descarga</span>
          </router-link>
        </div>
      </div>
    </div>

    <!-- Usuários - ADMIN, GERENTE e BANCA -->
    <router-link 
      v-if="hasPermission('users')" 
      to="/users" 
      :class="selectedRoute('/users') ? 'active' : ''"
    >
      <i class="fa-solid fa-users"></i>
      <span>Usuários</span>
    </router-link>

    <!-- Configurações de Região - ADMIN, GERENTE, BANCA e COBRADOR -->
    <router-link 
      v-if="hasPermission('regionsconfigs')" 
      to="/regionsconfigs" 
      :class="selectedRoute('/regionsconfigs') ? 'active' : ''"
    >
      <i class="fa-solid fa-sliders"></i>
      <span>Configurações da Região</span>
    </router-link>

    <!-- Aplicativos - disponível para todos os usuários autenticados -->
    <router-link
      to="/aplicativos"
      :class="selectedRoute('/aplicativos') ? 'active' : ''"
    >
      <i class="fa-solid fa-mobile-screen-button"></i>
      <span>Baixar App</span>
    </router-link>

    <router-link
      v-if="hasPermission('notifications')"
      to="/whatsapp"
      :class="selectedRoute('/whatsapp') ? 'active' : ''"
    >
      <i class="fa-solid fa-bell"></i>
      <span>Notificações</span>
    </router-link>

    <!-- Sistema com submenu - só ADMIN -->
    <div 
      v-if="hasPermission('settings')" 
      class="menu-group"
    >
      <div
        class="menu-title"
        :class="{ 'has-submenu': true, 'submenu-open': showSystemSubmenu }"
        @click="toggleSubmenu('system')"
      >
        <i class="fa-solid fa-gear"></i>
        <span>Sistema</span>
        <i class="fa-solid fa-chevron-right submenu-arrow" :class="{ 'rotated': showSystemSubmenu }"></i>
      </div>
      <div class="submenu" :class="{ 'show': showSystemSubmenu }">
        <div class="submenu-content">
          <router-link to="/tasks" class="submenu-item" :class="{ 'active': $route.path === '/tasks' }">
            <i class="fa-solid fa-list-check"></i>
            <span>Tarefas</span>
          </router-link>
          <router-link to="/app-versions" class="submenu-item" :class="{ 'active': $route.path === '/app-versions' }">
            <i class="fa-solid fa-mobile-alt"></i>
            <span>Versões do App</span>
          </router-link>
          <router-link to="/settings" class="submenu-item" :class="{ 'active': $route.path === '/settings' }">
            <i class="fa-solid fa-cog"></i>
            <span>Configurações</span>
          </router-link>
          <router-link to="/api-clients" class="submenu-item" :class="{ 'active': $route.path === '/api-clients' }">
            <i class="fa-solid fa-key"></i>
            <span>Clientes API</span>
          </router-link>
          <div class="submenu-item submenu-action" @click="handleClearCache" :class="{ 'loading': clearingCache }">
            <i class="fa-solid fa-trash-can"></i>
            <span>{{ clearingCache ? 'Limpando...' : 'Limpar cache' }}</span>
          </div>
          <router-link to="/whatsconfig" class="submenu-item" :class="{ 'active': $route.path === '/whatsconfig' }">
            <i class="fa-solid fa-mobile-screen-button"></i>
            <span>Instâncias WhatsApp</span>
          </router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { hasPermission } from '@/services/permissions';
import mainService from "@/services/mainService";
import { toast } from "@/composables/useToast";
import { handleErrorMessage } from "@/services/config";
import stateStore from "@/utils/store";

export default {
  data() {
    return {
      clearingCache: false,
      showSubmenu: false,
      showCollectorsSubmenu: false,
      showDescargasSubmenu: false,
      showBancasSubmenu: false,
      showSystemSubmenu: false,
      settings: null,
      currentLogo: null,
      isDarkTheme: false,
      allRegions: [], // Todas as regiões para verificação
    };
  },
  computed: {
    sidebarCollapsed() {
      return stateStore.sidebarCollapsed;
    },
    isHovering() {
      return stateStore.sidebarHoverExpanded;
    },
    showCollapsedFavicon() {
      return this.sidebarCollapsed && !this.isHovering && !!this.settings?.faviconUrl;
    },
    userRole() {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const role = parseInt(user.role);
      return isNaN(role) ? null : role;
    },
    isAgente() {
      return this.userRole === 4;
    },
    currentRegion() {
      const savedRegion = localStorage.getItem('currentRegion');
      return savedRegion ? JSON.parse(savedRegion) : null;
    },
    user() {
      return JSON.parse(localStorage.getItem('user') || '{}');
    },
    isAdmin() {
      return this.userRole === 0;
    },
    isBanca() {
      return this.userRole === 3;
    },
    // Determinar se deve mostrar "Descarga Enviada"
    // NÃO mostrar para Redescarga (OnlyDescarga=true E DescargaRegionId=null)
    shouldShowDescargaEnviada() {
      // Admin sempre vê
      if (this.isAdmin) {
        return true;
      }
      
      // Para BANCA: verificar se tem apenas regiões de redescarga
      if (this.isBanca) {
        // Se não há regiões carregadas, mostrar por padrão
        if (!this.allRegions || this.allRegions.length === 0) {
          return true;
        }
        
        // Verificar se todas as regiões são redescarga (OnlyDescarga=true E DescargaRegionId=null)
        const hasOnlyRedescargaRegions = this.allRegions.every(region => 
          region.onlyDescarga === true && !region.descargaRegionId
        );
        
        // Se tem apenas redescarga, NÃO mostrar (redescarga não envia, só recebe)
        if (hasOnlyRedescargaRegions) {
          return false;
        }
        
        return true;
      }
      
      // Para outros usuários, verificar a região atual
      if (!this.currentRegion) return true;
      
      // Se é Redescarga (OnlyDescarga=true E DescargaRegionId=null), NÃO mostrar
      if (this.currentRegion.onlyDescarga && !this.currentRegion.descargaRegionId) {
        return false;
      }
      
      return true;
    },
    // Determinar se deve mostrar "Descarga Recebida"
    // NÃO mostrar para Normal (OnlyDescarga=false)
    shouldShowDescargaRecebida() {
      // Admin sempre vê
      if (this.isAdmin) {
        return true;
      }
      
      // Para BANCA: verificar se tem pelo menos uma região de descarga (OnlyDescarga=true)
      if (this.isBanca) {
        // Se não há regiões carregadas, mostrar por padrão
        if (!this.allRegions || this.allRegions.length === 0) {
          return true;
        }
        
        // Verificar se a banca tem pelo menos uma região de descarga
        const hasDescargaRegion = this.allRegions.some(region => region.onlyDescarga === true);
        
        // Se não tem nenhuma região de descarga, NÃO mostrar (banca só tem região normal, só envia)
        return hasDescargaRegion;
      }
      
      // Para outros usuários, verificar a região atual
      if (!this.currentRegion) return true;
      
      // Se é Normal (OnlyDescarga=false), NÃO mostrar
      if (!this.currentRegion.onlyDescarga) {
        return false;
      }
      
      return true;
    }
  },
  async mounted() {
    await this.loadSettings();
    this.checkCurrentTheme();
    this.setCurrentLogo();
    
    // Carregar regiões para verificação de menu
    await this.loadRegionsForMenu();
    
    // Escutar mudanças de tema
    window.addEventListener('storage', this.handleThemeChange);
    
    // Observar mudanças no atributo data-theme do documento
    this.themeObserver = new MutationObserver(this.handleThemeChange);
    this.themeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme']
    });
  },
  beforeUnmount() {
    window.removeEventListener('storage', this.handleThemeChange);
    if (this.themeObserver) {
      this.themeObserver.disconnect();
    }
  },
  methods: {
    toggleSubmenu(menuKey) {
      const map = {
        bancas: "showBancasSubmenu",
        collectors: "showCollectorsSubmenu",
        sellers: "showSubmenu",
        descargas: "showDescargasSubmenu",
        system: "showSystemSubmenu",
      };
      const target = map[menuKey];
      if (!target) return;

      const willOpen = !this[target];
      // Fecha os outros; o clicado alterna (abre/recolhe)
      Object.values(map).forEach((key) => {
        this[key] = key === target ? willOpen : false;
      });
    },
    handleSidebarMouseEnter() {
      if (this.sidebarCollapsed) {
        stateStore.sidebarHoverExpanded = true;
      }
    },

    handleSidebarMouseLeave() {
      stateStore.sidebarHoverExpanded = false;
    },

    selectedRoute(routeName) {
      if (routeName === '/dashboard') {
        return this.$route.path === '/dashboard' || this.$route.path === '/';
      }
      return this.$route.path === routeName;
    },
    
    hasPermission(menuKey) {
      return hasPermission(this.userRole, menuKey);
    },

    async loadSettings() {
      try {
        const response = await mainService.getActiveSettings();
        if (response.data) {
          this.settings = response.data;
          this.setCurrentLogo(); // Atualizar logo após carregar configurações
        }
      } catch (error) {
        console.log("Nenhuma configuração ativa encontrada");
        this.settings = null;
      }
    },

    checkCurrentTheme() {
      this.isDarkTheme = document.documentElement.hasAttribute('data-theme') && 
                        document.documentElement.getAttribute('data-theme') === 'dark';
    },

    setCurrentLogo() {
      if (!this.settings) return;
      
      this.checkCurrentTheme();
      
      if (this.isDarkTheme && this.settings.logoDark) {
        this.currentLogo = this.settings.logoDark;
      } else if (!this.isDarkTheme && this.settings.logoLight) {
        this.currentLogo = this.settings.logoLight;
      } else {
        // Fallback: tentar usar a logo disponível
        this.currentLogo = this.settings.logoLight || this.settings.logoDark || null;
      }
    },

    handleThemeChange() {
      this.setCurrentLogo();
    },

    handleImageError(event) {
      // Fallback para a outra logo se disponível
      if (this.isDarkTheme && this.settings?.logoLight) {
        event.target.src = this.settings.logoLight;
      } else if (!this.isDarkTheme && this.settings?.logoDark) {
        event.target.src = this.settings.logoDark;
      }
    },

    async handleClearCache() {
      if (this.clearingCache) return;
      try {
        const confirmed = await this.$confirm({
          title: 'Limpar cache',
          message: 'Deseja limpar todo o cache do sistema? Os dados serão recarregados nas próximas requisições.'
        });
        if (!confirmed) return;
        this.clearingCache = true;
        const res = await mainService.clearAllCache();
        const msg = res?.data?.message || 'Cache limpo com sucesso!';
        toast.success(msg);
      } catch (err) {
        if (err !== false) {
          toast.error(handleErrorMessage(err));
        }
      } finally {
        this.clearingCache = false;
      }
    },

    async loadRegionsForMenu() {
      try {
        // Buscar todas as regiões
        const allRegionsResponse = await mainService.searchRegions();
        const allRegions = allRegionsResponse.data || [];
        
        // Se for BANCA, filtrar apenas as regiões da banca
        if (this.isBanca && this.user && this.user.id) {
          try {
            const bancaResponse = await mainService.getUserBancaId(this.user.id);
            const userBancaId = bancaResponse.data;
            
            if (userBancaId) {
              this.allRegions = allRegions.filter(region => region.bancaId === userBancaId);
            } else {
              this.allRegions = [];
            }
          } catch (error) {
            this.allRegions = [];
          }
        } else if (this.isAdmin) {
          // Para ADMIN, carregar regiões da banca selecionada
          const selectedBanca = localStorage.getItem('selectedBanca');
          if (selectedBanca) {
            try {
              const banca = JSON.parse(selectedBanca);
              this.allRegions = allRegions.filter(region => region.bancaId === banca.id);
            } catch (e) {
              this.allRegions = allRegions;
            }
          } else {
            this.allRegions = allRegions;
          }
        } else {
          // Para outros usuários (GERENTE/COBRADOR), carregar suas regiões
          try {
            const userRegionsResponse = await mainService.getUserRegions(this.user.id);
            const userRegions = userRegionsResponse.data || [];
            const regionIds = userRegions.map(r => r.id);
            
            if (this.user.regionId && !regionIds.includes(this.user.regionId)) {
              regionIds.push(this.user.regionId);
            }
            
            this.allRegions = allRegions.filter(region => regionIds.includes(region.id));
          } catch (error) {
            this.allRegions = [];
          }
        }
      } catch (error) {
        this.allRegions = [];
      }
    }
  },
};
</script>

<style scoped>
/* Garantir que os ícones FontAwesome sejam exibidos corretamente */
.sidebar [class*="fa-"]:before {
  font-family: "Font Awesome 6 Free", "Font Awesome 6 Pro", FontAwesome !important;
  font-weight: 900 !important;
  display: inline-block !important;
}

.sidebar {
  position: fixed;
  width: 260px;
  height: 100vh;
  background: var(--bg-secondary);
  border-right: 1px solid var(--card-border);
  transition: var(--transition);
  z-index: 200;
  backdrop-filter: blur(10px);
  box-shadow: 4px 0 20px rgba(0, 0, 0, 0.1);
  overflow-y: auto;
  overflow-x: hidden;
}

/* Customização da barra de scroll */
.sidebar::-webkit-scrollbar {
  width: 6px;
}

.sidebar::-webkit-scrollbar-track {
  background: transparent;
}

.sidebar::-webkit-scrollbar-thumb {
  background: var(--text-muted);
  border-radius: 3px;
  opacity: 0.5;
  transition: all 0.3s ease;
}

.sidebar::-webkit-scrollbar-thumb:hover {
  background: var(--accent-primary);
  opacity: 0.8;
}

/* Para Firefox */
.sidebar {
  scrollbar-width: thin;
  scrollbar-color: var(--text-muted) transparent;
}

.sidebar header {
  font-size: 24px;
  color: var(--text-primary);
  line-height: 60px;
  text-align: center;
  background: linear-gradient(135deg, var(--bg-tertiary), var(--bg-secondary));
  border-bottom: 1px solid var(--card-border);
  user-select: none;
  font-family: "Inter", sans-serif;
  font-weight: 700;
  transition: var(--transition);
  position: relative;
}

.sidebar header::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 20%;
  right: 20%;
  height: 2px;
  background: linear-gradient(90deg, transparent, var(--accent-primary), transparent);
}

.header-logo {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 8px;
}

.logo-image {
  max-width: 200px;
  max-height: 44px;
  width: auto;
  height: auto;
  object-fit: contain;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1));
  transition: all 0.3s ease;
}

.logo-icon-collapsed {
  max-width: 32px;
  max-height: 32px;
  width: auto;
  height: auto;
  object-fit: contain;
  transition: all 0.3s ease;
}

/* O favicon é um ícone preto sólido; no tema escuro fica invisível sobre o fundo escuro */
[data-theme="dark"] .logo-icon-collapsed {
  filter: invert(1);
}

.logo-image:hover {
  transform: scale(1.02);
  filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.15));
}

.header-text {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
}

/* Remove sublinhado de todos os links */
.sidebar a,
.menu-title,
.submenu-item,
.sidebar a:visited,
.sidebar a:hover,
.sidebar a:active,
.sidebar a:link {
  text-decoration: none;
  color: var(--text-primary);
  transition: var(--transition);
}

.sidebar a,
.menu-title {
  display: flex;
  align-items: center;
  height: 3rem;
  width: 100%;
  padding: 0 20px;
  box-sizing: border-box;
  border-left: 3px solid transparent;
  font-family: "Inter", sans-serif;
  cursor: pointer;
  transition: var(--transition);
  margin: 2px 0;
  position: relative;
}

a.active,
a:hover,
.menu-title:hover {
  background: linear-gradient(90deg, var(--glow-color), transparent);
  border-left: 3px solid var(--accent-primary);
  color: var(--accent-primary);
  margin-right: 8px;
}

a.active::before,
a:hover::before,
.menu-title:hover::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  background: var(--accent-primary);
  box-shadow: 0 0 10px var(--accent-primary);
}

.sidebar a i,
.menu-title i {
  font-size: 18px;
  margin-right: 16px;
  width: 20px;
  text-align: center;
  font-family: "Font Awesome 6 Free", "Font Awesome 6 Pro", FontAwesome;
  font-weight: 900;
  display: inline-block;
}

/* Ícones brands (WhatsApp, etc) */
.sidebar a i.fab,
.sidebar a i.fa-brands,
.submenu-item i.fab,
.submenu-item i.fa-brands {
  font-family: "Font Awesome 6 Brands", "Font Awesome 5 Brands", FontAwesome;
  font-weight: 400;
}

.sidebar a span,
.menu-title span {
  font-size: 14px;
  font-weight: 500;
}

/* Submenu */
.menu-group {
  position: relative;
}

.menu-title.has-submenu {
  justify-content: space-between;
  cursor: pointer;
  user-select: none;
}

.menu-title.submenu-open {
  background: linear-gradient(90deg, var(--glow-color), transparent);
  border-left: 3px solid var(--accent-primary);
  color: var(--accent-primary);
  margin-right: 8px;
}

.submenu-arrow {
  font-size: 12px;
  transition: transform 0.3s ease;
  margin-left: auto;
  font-family: "Font Awesome 6 Free", "Font Awesome 6 Pro", FontAwesome;
  font-weight: 900;
  display: inline-block;
}

.submenu-arrow.rotated {
  transform: rotate(90deg);
}

.submenu {
  position: static;
  width: calc(100% - 20px);
  max-height: 0;
  opacity: 0;
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  margin-left: 20px;
  margin-right: 0px;
}

.submenu.show {
  max-height: 500px;
  opacity: 1;
  margin-top: 5px;
  margin-bottom: 5px;
}

.submenu-content {
  background: transparent;
  overflow: hidden;
  position: relative;
}

.submenu-item {
  display: flex;
  align-items: center;
  text-align: left;
  padding: 12px 15px;
  font-size: 14px;
  color: var(--text-secondary);
  transition: var(--transition);
  position: relative;
  margin: 1px 0;
  width: 100%;
  box-sizing: border-box;
}

.submenu-item i,
.submenu-item svg {
  font-size: 14px;
  margin-right: 12px;
  width: 16px;
  text-align: center;
  color: var(--text-muted);
  transition: var(--transition);
  display: inline-block;
  vertical-align: middle;
  flex-shrink: 0;
}

.submenu-item i {
  font-family: "Font Awesome 6 Free", "Font Awesome 6 Pro", FontAwesome;
  font-weight: 900;
}

.submenu-item i.fab {
  font-family: "Font Awesome 6 Brands", "Font Awesome 5 Brands", FontAwesome;
  font-weight: 400;
}

.submenu-item:hover {
  background: var(--glow-color);
  color: var(--accent-primary);
}

.submenu-item:hover i,
.submenu-item:hover svg {
  color: var(--accent-primary);
  transform: scale(1.1);
}

.submenu-item.active {
  background: var(--glow-color);
  color: var(--accent-primary);
  border-left: 3px solid var(--accent-primary);
}

.submenu-item.active i,
.submenu-item.active svg {
  color: var(--accent-primary);
}

.submenu-item span {
  text-transform: none;
  letter-spacing: normal;
  font-weight: 500;
}

.submenu-action {
  display: flex;
  align-items: center;
  width: 100%;
  height: 3rem;
  padding: 0 20px;
  box-sizing: border-box;
  cursor: pointer;
  border: none;
  background: none;
  color: var(--text-secondary);
  font-family: "Inter", sans-serif;
  font-size: 14px;
  text-align: left;
}

.submenu-action.loading {
  opacity: 0.7;
  pointer-events: none;
}

/* Responsivo */
@media (max-width: 860px) {
  .sidebar {
    width: 70px;
    overflow-y: auto;
    overflow-x: hidden;
  }
  
  /* Ajustar barra de scroll para tela menor */
  .sidebar::-webkit-scrollbar {
    width: 4px;
  }

  .sidebar header {
    padding: 4px;
  }

  .header-logo {
    padding: 4px;
  }

  .logo-image {
    max-width: 50px;
    max-height: 30px;
  }

  .header-text {
    font-size: 14px;
    writing-mode: vertical-rl;
    text-orientation: mixed;
  }

  .menu-title span,
  .sidebar a span,
  .submenu-item span {
    display: none;
  }

  .sidebar a i,
  .menu-title i {
    margin-left: 10px;
  }

  .submenu-arrow {
    display: none;
  }

  .submenu {
    margin-left: 10px;
    margin-right: 0px;
    width: calc(100% - 10px);
  }

  .menu-title.has-submenu {
    justify-content: center;
  }
}

/* Sidebar recolhida manualmente (botão no header) - somente ícones */
.sidebar.collapsed {
  width: 70px;
}

.sidebar.collapsed .header-text,
.sidebar.collapsed .menu-title span,
.sidebar.collapsed a span,
.sidebar.collapsed .submenu-item span,
.sidebar.collapsed .submenu-arrow {
  display: none;
}

.sidebar.collapsed .logo-image {
  max-width: 50px;
  max-height: 30px;
}

.sidebar.collapsed a i,
.sidebar.collapsed .menu-title i {
  margin-right: 0;
}

.sidebar.collapsed .menu-title.has-submenu {
  justify-content: center;
}

.sidebar.collapsed .submenu {
  display: none;
}

/* Expande temporariamente ao passar o mouse, sem empurrar o conteúdo */
.sidebar.collapsed.hovered {
  width: 260px;
}

.sidebar.collapsed.hovered .header-text,
.sidebar.collapsed.hovered .menu-title span,
.sidebar.collapsed.hovered a span,
.sidebar.collapsed.hovered .submenu-item span,
.sidebar.collapsed.hovered .submenu-arrow {
  display: inline-block;
}

.sidebar.collapsed.hovered .logo-image {
  max-width: 200px;
  max-height: 44px;
}

.sidebar.collapsed.hovered a i,
.sidebar.collapsed.hovered .menu-title i {
  margin-right: 16px;
}

.sidebar.collapsed.hovered .menu-title.has-submenu {
  justify-content: space-between;
}

.sidebar.collapsed.hovered .submenu {
  display: block;
}
</style>

